import React, { FunctionComponent, useMemo } from 'react';
import { CommentOutlined, EyeOutlined, HeartOutlined, ReadFilled } from '@ant-design/icons';
import { Button, Spin } from 'antd';
import IPost from '@interfaces/IPost';
import useTranslate from '@hooks/useTranslate';
import ViewFiles from '@components/ViewFiles/ViewFiles';
import componentContent from '../../../components/PostCard/PostCard.module.json';
import css from './PostPreview.module.less';

interface PostPreviewProps {
  post: IPost | null;
  isLoadingBlock?: boolean;
  isLoadingHide?: boolean;
  isLoadingFace?: boolean;
  isDisabledBlock?: boolean;
  isDisabledHide?: boolean;
  isDisabledFace?: boolean;
  onBlock?: (e: React.MouseEvent<HTMLElement, MouseEvent>, postID: IPost['id']) => void;
  onHide?: (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
    postID: IPost['id'],
    hide: boolean,
  ) => void;
  onFace?: (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
    postID: IPost['id'],
    face: boolean,
  ) => void;
  onOpen?: (e: React.MouseEvent<HTMLElement, MouseEvent>, postID: IPost['id']) => void;
}

const PostPreview: FunctionComponent<PostPreviewProps> = (props) => {
  const {
    post,
    isLoadingBlock,
    isLoadingHide,
    isLoadingFace,
    isDisabledBlock,
    isDisabledHide,
    isDisabledFace,
    onBlock,
    onHide,
    onFace,
    onOpen,
  } = props;

  if (!post) return null;

  const content = useTranslate(componentContent, false);

  const buttonHideText = useMemo(() => {
    return post.isHidden ? content.unshadowbanShort : content.shadowbanShort;
  }, [post.isHidden, content]);

  const buttonFaceText = useMemo(() => {
    if (!post.marks || post.marks.length === 0) {
      return content.face;
    }
    return post.marks.some((item) => item?.markType === 'FACE') ? content.notface : content.face;
  }, [post.marks, content]);

  const handleBlock = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    if (onBlock) {
      onBlock(e, post.id);
      onBlock(e, post.id);
    }
  };

  const handleHide = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    if (onHide) {
      onHide(e, post.id, !post.isHidden);
    }
  };

  const handleFace = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    if (post.marks?.length && post.marks?.filter((item) => item?.markType === 'FACE').length) {
      if (onFace) onFace(e, post.id, false);
    } else {
      if (onHide) onHide(e, post.id, true);
      if (onFace) onFace(e, post.id, true);
    }
  };

  const handleOpen = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (onOpen) {
      onOpen(e, post.id);
    }
  };

  const buttonFaceType = useMemo(() => {
    if (!post.marks || post.marks.length === 0) {
      return 'default';
    }
    return post.marks.some((item) => item?.markType === 'FACE') ? 'primary' : 'default';
  }, [post.marks]);

  const buttonHideType = useMemo(() => {
    return post.isHidden ? 'primary' : 'default';
  }, [post.isHidden]);

  const buttonBlockedType = useMemo(() => {
    return post.isBlocked ? 'primary' : 'default';
  }, [post.isBlocked]);

  return (
    <Spin spinning={isLoadingHide || isLoadingBlock || isLoadingFace}>
      <div className={css.root}>
        <div className={css.overlayContainer}>
          <div
            className={css.overlay}
            onClick={handleOpen}
          >
            <div className={css.overlayItem}>
              <ReadFilled />
            </div>
          </div>
          <ViewFiles
            files={post.files}
            altText={post?.text}
          />
        </div>
        <div className={css.footer}>
          <div className={css.footerInfo}>
            <div className={css.footerInfoItem}>
              <HeartOutlined /> {post.likesCount}
            </div>
            <div className={css.footerInfoItem}>
              <CommentOutlined /> {post.commentsCount}
            </div>
            <div className={css.footerInfoItem}>
              <EyeOutlined /> {post.viewsCount}
            </div>
          </div>
          <div className={css.footerAction}>
            <Button
              className={css.footerActionButton}
              size='small'
              type={buttonBlockedType}
              disabled={isDisabledBlock}
              onClick={handleBlock}
            >
              {content.block}
            </Button>
            <Button
              className={css.footerActionButton}
              size='small'
              type={buttonHideType}
              disabled={isDisabledHide}
              onClick={handleHide}
            >
              {buttonHideText}
            </Button>
            <Button
              className={css.footerActionButton}
              size='small'
              type={buttonFaceType}
              disabled={isDisabledFace}
              onClick={handleFace}
            >
              {buttonFaceText}
            </Button>
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default PostPreview;
